import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { QuestionLibraryCatalogCourse } from 'services/api/adminContentClient';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { cn } from 'lib/utils';
import { courseMetaLine, groupCoursesForBuilders } from '../courseGrouping';

export type CourseSwitcherProps = {
  courses: QuestionLibraryCatalogCourse[];
  loading: boolean;
  error: string;
  onRetry: () => void;
  selectedCourseId: number | null;
  /** Shown on the button before the catalog has the open course (e.g. a deep link outside the catalog). */
  fallbackName?: string;
  /** Called with the chosen course; the menu closes itself first. */
  onSelect: (courseId: number) => void;
};

const CourseRow: React.FC<{
  course: QuestionLibraryCatalogCourse;
  active: boolean;
  onPick: () => void;
}> = ({ course, active, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    aria-current={active ? 'true' : undefined}
    data-testid={`course-switcher-item-${course.id}`}
    className={cn(
      'flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-left text-sm hover:bg-foundation-calm-blue-50',
      active && 'bg-foundation-calm-blue-50 font-semibold',
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'h-2.5 w-2.5 shrink-0 rounded-[3px]',
        course.looks_like_test ? 'bg-foundation-base-darkbase-dark-100' : 'bg-foundation-orange-500',
      )}
    />
    <span className="min-w-0 flex-1">
      <span className="block truncate">{course.name}</span>
      <span className="block truncate text-xs font-normal text-muted-foreground">{courseMetaLine(course)}</span>
    </span>
  </button>
);

const GroupLabel: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <p className="px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
    {label} <span className="font-normal">{count}</span>
  </p>
);

/**
 * Course Builder's course picker for an open course. Same button and menu as the Question Library and
 * Module Builder course pickers: the open course on the button; a searchable, grouped course menu.
 */
export default function CourseSwitcher({
  courses,
  loading,
  error,
  onRetry,
  selectedCourseId,
  fallbackName,
  onSelect,
}: CourseSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [showTests, setShowTests] = React.useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = React.useState<number | undefined>(undefined);
  const pickerRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const grouped = React.useMemo(() => groupCoursesForBuilders(courses), [courses]);
  const activeCourse = courses.find(course => course.id === selectedCourseId);
  const term = search.trim().toLowerCase();
  const matches = (course: QuestionLibraryCatalogCourse) => !term || course.name.toLowerCase().includes(term);
  const assigned = grouped.assigned.filter(matches);
  const other = grouped.other.filter(matches);
  const tests = grouped.tests.filter(matches);
  const testsOpen = showTests || !!term;

  const pick = (courseId: number) => {
    setOpen(false);
    setSearch('');
    // The chosen row unmounts with the menu; keep keyboard focus on the picker button.
    buttonRef.current?.focus();
    if (courseId !== selectedCourseId) onSelect(courseId);
  };

  const toggle = () => {
    if (!open && buttonRef.current) {
      // Keep the menu above the bottom navigation and floating save bar (fixed below lg, ~11rem tall together).
      const reserved = window.matchMedia?.('(min-width: 1024px)')?.matches ? 24 : 176;
      const below = window.innerHeight - buttonRef.current.getBoundingClientRect().bottom - 8 - reserved;
      setMenuMaxHeight(Math.max(240, Math.min(below, window.innerHeight * 0.7)));
    }
    setOpen(value => !value);
  };

  const label = activeCourse?.name ?? fallbackName ?? 'Choose a course';
  const meta = activeCourse ? courseMetaLine(activeCourse) : '';

  return (
    <div className="relative min-w-0 w-full sm:w-[26rem]" ref={pickerRef}>
      <button
        type="button"
        ref={buttonRef}
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? 'course-switcher-menu' : undefined}
        aria-label={`Switch course: ${label}`}
        data-testid="course-switcher"
        className="flex w-full items-center gap-3 rounded-2xl bg-white px-3 py-2.5 text-left shadow-sm"
      >
        <span
          aria-hidden="true"
          className={cn(
            'grid h-8 w-8 shrink-0 place-items-center rounded-xl text-sm font-bold text-white',
            activeCourse?.looks_like_test ? 'bg-foundation-base-darkbase-dark-300' : 'bg-foundation-orange-500',
          )}
        >
          {(label.trim()[0] ?? '·').toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-foreground">{label}</span>
          {meta ? <span className="block truncate text-xs text-muted-foreground">{meta}</span> : null}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
      {open ? (
        <div
          id="course-switcher-menu"
          className="absolute left-0 top-full z-[60] mt-2 flex max-h-[70vh] w-[min(28rem,calc(100vw-3rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
          style={menuMaxHeight ? { maxHeight: menuMaxHeight } : undefined}
          data-testid="course-switcher-menu"
        >
          <div className="p-3 pb-2">
            <Input
              autoFocus
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={`Find a course (${courses.length})`}
              aria-label="Find a course"
            />
          </div>
          <div className="min-h-0 overflow-y-auto px-2 pb-3" data-testid="course-switcher-list">
            {error ? (
              <p role="alert" className="px-2.5 py-2 text-sm text-destructive">
                {error}{' '}
                <Button size="auto" variant="ghost" onClick={onRetry}>Retry course list</Button>
              </p>
            ) : null}
            {loading ? <div className="flex justify-center py-8"><LoadingAnimation /></div> : null}
            {assigned.length ? (
              <>
                <GroupLabel label="Assigned to learners" count={assigned.length} />
                {assigned.map(course => (
                  <CourseRow key={course.id} course={course} active={course.id === selectedCourseId} onPick={() => pick(course.id)} />
                ))}
              </>
            ) : null}
            {other.length ? (
              <>
                <GroupLabel label="Other courses" count={other.length} />
                {other.map(course => (
                  <CourseRow key={course.id} course={course} active={course.id === selectedCourseId} onPick={() => pick(course.id)} />
                ))}
              </>
            ) : null}
            {grouped.tests.length > 0 && (tests.length > 0 || !term) ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowTests(value => !value)}
                  aria-expanded={testsOpen}
                  data-testid="course-switcher-tests-toggle"
                  className="mt-3 flex w-full items-center gap-1.5 px-2.5 pb-1 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {testsOpen ? <ChevronDown className="h-3 w-3" aria-hidden="true" /> : <ChevronRight className="h-3 w-3" aria-hidden="true" />}
                  Looks like a test or old copy <span className="font-normal">{tests.length}</span>
                </button>
                {testsOpen
                  ? tests.map(course => (
                      <CourseRow key={course.id} course={course} active={course.id === selectedCourseId} onPick={() => pick(course.id)} />
                    ))
                  : null}
              </>
            ) : null}
            {term && !assigned.length && !other.length && !tests.length ? (
              <p className="px-2.5 py-3 text-sm text-muted-foreground">No courses match.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
